import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2, Mail, Lock, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirectQuery = params.get("redirect");
  const from = redirectQuery || location.state?.from?.pathname || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + (redirectQuery || "/dashboard");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <section data-testid="login-page" className="min-h-[80vh] bg-brand-cream flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/assets/logo_hires.png" alt="414, LLC" className="w-20 h-20 object-contain mx-auto mb-3" />
          <h1 className="font-serif text-4xl text-brand-moss">Welcome back.</h1>
          <p className="mt-2 text-brand-moss/70 text-sm">Sign in to your Circle of Intention dashboard.</p>
        </div>

        <button
          type="button"
          onClick={googleSignIn}
          data-testid="login-google-btn"
          className="w-full flex items-center justify-center gap-3 bg-white border border-brand-moss/25 rounded-full py-3.5 text-sm text-brand-moss hover:bg-brand-parchment transition-colors shadow-sm"
        >
          <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.6 2.2 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.5 13.2 17.8 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.5 24.5c0-1.5-.1-3-.4-4.5H24v9h12.7c-.5 3-2.3 5.5-4.9 7.2l7.6 5.9c4.5-4.1 7.1-10.2 7.1-17.6z" />
            <path fill="#FBBC05" d="M10.5 28.7C10.1 27.6 10 26.3 10 25s.1-2.6.5-3.7l-7.9-6.1C.9 18.3 0 21.5 0 25s.9 6.7 2.6 9.8l7.9-6.1z" />
            <path fill="#34A853" d="M24 50c6.1 0 11.2-2 14.9-5.5l-7.6-5.9c-2.1 1.4-4.8 2.3-7.4 2.3-6.2 0-11.5-3.7-13.4-8.8l-7.9 6.1C6.5 44.6 14.6 50 24 50z" />
          </svg>
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-brand-moss/40">
          <span className="h-px flex-1 bg-brand-moss/15" /> or <span className="h-px flex-1 bg-brand-moss/15" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.25em] text-brand-moss/60 flex items-center gap-2"><Mail className="w-3 h-3" /> Email</span>
            <input
              data-testid="login-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="mt-2 w-full bg-brand-parchment border border-brand-moss/15 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-gold transition-colors"
            />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.25em] text-brand-moss/60 flex items-center gap-2"><Lock className="w-3 h-3" /> Password</span>
            <input
              data-testid="login-password"
              type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="mt-2 w-full bg-brand-parchment border border-brand-moss/15 rounded-xl px-4 py-3 focus:outline-none focus:border-brand-gold transition-colors"
            />
          </label>
          <button
            type="submit" disabled={loading}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-moss text-brand-gold hover:bg-brand-moss-light px-6 py-3.5 rounded-full text-sm tracking-wide transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {loading ? "Signing you in…" : "Sign in"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-brand-moss/70">
          New to the Circle?{" "}
          <Link to="/register" data-testid="link-register" className="text-brand-moss font-medium border-b border-brand-gold pb-0.5">
            Create an account
          </Link>
        </p>
      </div>
    </section>
  );
}
